// Yes/no weeks body — Yes days and No days per Monday-anchored week, stacked,
// for a scalar `bool` feature. The secondary chart after BoolCalendarBody.
//
// Each day is first decided under the `any` rule (boolAggregation), so
// a column counts days, not checkins, and never exceeds 7. Whatever a column
// is short of 7 was not recorded — never folded into No.

import React, { useEffect, useMemo, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Rectangle } from 'recharts';
import { api } from '../../api/client';
import { boolLabels } from '../../utils/boolFeature';
import type { ChartBodyProps } from '../types';
import { useTimeRangeState, windowForAnchor, isoToLocalMs, type Granularity } from '../windowRange';
import ChartTimeRangePicker from '../ChartTimeRangePicker';
import { BAR_TICK, BAR_TOOLTIP_CURSOR, BarTooltipCard, barChartMargin } from './barChartStyles';
import { boolDayAnswers, boolWeeks, type BoolValueRow, type BoolWeek } from './boolAggregation';
import { BOOL_COLORS } from './chartColors';

// A single week is one column; W adds nothing over the calendar.
const GRANULARITIES: readonly Granularity[] = ['M', '6M', 'Y', 'All'];
const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const Y_TICKS = [0, 7];
// The 2px surface gap between stacked segments (dataviz mark spec): a white
// stroke straddles each edge by 1px.
const SEGMENT_GAP = { stroke: '#ffffff', strokeWidth: 2 };

function weekLabel(weekStart: string): string {
  const [y, m, d] = weekStart.split('-').map(Number);
  const s = new Date(y, m - 1, d);
  return `${MONTH_SHORT[s.getMonth()]} ${s.getDate()}`;
}

function weekRangeLabel(weekStart: string): string {
  const [y, m, d] = weekStart.split('-').map(Number);
  const s = new Date(y, m - 1, d);
  const e = new Date(y, m - 1, d + 6);
  return s.getMonth() === e.getMonth()
    ? `${MONTH_SHORT[s.getMonth()]} ${s.getDate()}–${e.getDate()}`
    : `${MONTH_SHORT[s.getMonth()]} ${s.getDate()} – ${MONTH_SHORT[e.getMonth()]} ${e.getDate()}`;
}

const days = (n: number) => `${n} day${n === 1 ? '' : 's'}`;

// Only the top of a column gets the rounded data-end: Yes is the top when the
// week has no No days.
type SegmentProps = React.ComponentProps<typeof Rectangle> & { payload?: BoolWeek };
const YesSegment = (props: unknown) => {
  const p = props as SegmentProps;
  return <Rectangle {...p} {...SEGMENT_GAP} radius={p.payload && p.payload.no === 0 ? [4, 4, 0, 0] : 0} />;
};
const NoSegment = (props: unknown) => (
  <Rectangle {...(props as SegmentProps)} {...SEGMENT_GAP} radius={[4, 4, 0, 0]} />
);

const BoolWeeksBody: React.FC<ChartBodyProps> = ({ featureId, config, fullscreen }) => {
  const [rows, setRows] = useState<BoolValueRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [trueLabel, falseLabel] = boolLabels(config?.params);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get<BoolValueRow[]>(`/api/features/values/${featureId}`)
      .then((resp) => { if (!cancelled) { setRows(resp.data); setError(null); } })
      .catch((err) => {
        console.error('Failed to fetch feature values:', err);
        if (!cancelled) setError('Failed to load data');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [featureId]);

  const answers = useMemo(() => boolDayAnswers(rows, 'any'), [rows]);
  const dataDates = useMemo(() => [...answers.keys()].map(isoToLocalMs), [answers]);
  const range = useTimeRangeState(dataDates, '6M');
  const win = useMemo(
    () => windowForAnchor(range.granularity, range.anchor),
    [range.granularity, range.anchor],
  );
  const weeks = useMemo(() => boolWeeks(answers, win), [answers, win]);
  const anyInView = weeks.some((w) => w.yes + w.no > 0);

  if (loading) {
    return (
      <div className={`flex justify-center items-center ${fullscreen ? 'h-full' : 'h-64'}`}>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600" />
      </div>
    );
  }
  if (error) {
    return <div className="text-center py-8 text-rose-500"><p>{error}</p></div>;
  }
  if (answers.size === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <p>Nothing recorded yet.</p>
      </div>
    );
  }

  return (
    <div className={fullscreen ? 'h-full flex flex-col' : 'flex flex-col'} style={{ touchAction: 'none' }}>
      <ChartTimeRangePicker state={range} granularities={GRANULARITIES} />
      <div className="px-1 mb-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-600">
        <span className="inline-flex items-center gap-1">
          <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: BOOL_COLORS.yes }} />
          {trueLabel}
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: BOOL_COLORS.no }} />
          {falseLabel}
        </span>
      </div>
      <div className={fullscreen ? 'flex-1 min-h-0' : 'h-80'}>
        {!anyInView ? (
          <div className="flex items-center justify-center h-full text-center text-gray-500">
            <p>Nothing recorded in this period.</p>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={weeks} margin={barChartMargin(fullscreen, 'numeric')}>
              <XAxis
                dataKey="weekStart"
                tickFormatter={weekLabel}
                tick={BAR_TICK}
                tickLine={false}
                minTickGap={12}
              />
              <YAxis
                domain={[0, 7]}
                ticks={Y_TICKS}
                allowDecimals={false}
                tick={BAR_TICK}
                tickLine={false}
              />
              <Tooltip
                cursor={BAR_TOOLTIP_CURSOR}
                content={({ active, payload }) => {
                  if (!active || !payload || payload.length === 0) return null;
                  const w = payload[0].payload as BoolWeek;
                  return (
                    <BarTooltipCard>
                      <div className="text-gray-600">{weekRangeLabel(w.weekStart)}</div>
                      <div className="text-gray-900">{trueLabel}: {days(w.yes)}</div>
                      <div className="text-gray-900">{falseLabel}: {days(w.no)}</div>
                    </BarTooltipCard>
                  );
                }}
              />
              <Bar
                dataKey="yes"
                name={trueLabel}
                stackId="days"
                fill={BOOL_COLORS.yes}
                maxBarSize={24}
                shape={YesSegment}
                isAnimationActive={false}
              />
              <Bar
                dataKey="no"
                name={falseLabel}
                stackId="days"
                fill={BOOL_COLORS.no}
                maxBarSize={24}
                shape={NoSegment}
                isAnimationActive={false}
              />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
};

export default BoolWeeksBody;
